import React, { useState, useMemo, useContext, useEffect } from 'react'
import { View, StyleSheet } from 'react-native'
import { Text, Button, Spinner, CheckBox } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { 
  productsDictAtom,
  productCategoriesDictAtom,
  suppliersDictAtom,
  storesDictAtom,
  productUnitsDictAtom,
  userRoleAtom,
  productsByBarcodeDictAtom
} from '../../global/AtomsProvider'
import { updateProduct } from '../../global/Firebase/firebase'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import HeaderIconButton from '../../components/HeaderIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { useToast } from 'react-native-toast-notifications'
import { validateProduct } from '../../utils/productValidation'
// ATL-968: the grid renders in catalog order, not dict-insertion order.
import { getProductsList } from '../../utils/productListHelpers'


export default function BatchProductEditScreen({ navigation }) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productsDict = useAtomValue(productsDictAtom)
  const categoriesDict = useAtomValue(productCategoriesDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom) 
  const storesDict = useAtomValue(storesDictAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const productsByBarcodeDict = useAtomValue(productsByBarcodeDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [editedProducts, setEditedProducts] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [visibleFields, setVisibleFields] = useState([
    'name',
    'categoryID',
    'supplierID',
    'purchasePrice',
    'packagePrice',
    'productUnit',
    'barcode',
    'isHiddenInNewSale',
    ...Object.keys(storesDict).map(storeId => `salePrices.${storeId}`),
    ...Object.keys(storesDict).map(storeId => `activeInStore.${storeId}`)
  ])
  const toast = useToast()

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
        headerRight: () => (
          <Button
            onPress={handleSave}
            disabled={Object.keys(editedProducts).length === 0 || isSaving}
            accessoryLeft={isSaving ? Spinner : undefined}
            size='small'
            style={styles.headerButton}
          >
            {t('save')}
          </Button>
        )
      })
    }
    if (!userRole?.permissions?.['Products'] && context.user?.role !== 'admin') {
      navigation.navigate('Home')
    }
  }, [editedProducts, isSaving])

  // ATL-968: the ordered catalog is memoized on its OWN, separately from the filters.
  // getProductsList sorts the whole catalog, and searchQuery changes on every keystroke —
  // folding the two into one memo re-sorts a 3,400-product org per character typed to
  // produce an order that did not change. Same reason as the memo in ProductSearch.js.
  const orderedProducts = useMemo(() => getProductsList(productsDict), [productsDict])

  const filteredProducts = useMemo(() => {
    return orderedProducts.filter(product => {
      const matchesCategory = !selectedCategory || product.categoryID === selectedCategory.id
      const matchesSupplier = !selectedSupplier || product.supplierID === selectedSupplier.id
      const matchesSearch = !searchQuery || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      
      return matchesCategory && matchesSupplier && matchesSearch
    })
  }, [orderedProducts, selectedCategory, selectedSupplier, searchQuery])

  const handleCellEdit = (productId, field, value) => {
    setEditedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      for (const [productId, updates] of Object.entries(editedProducts)) {
        const product = productsDict[productId]
        if (product) {
          const filteredUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
            if (key === 'salePrices') {
              const visibleSalePrices = Object.entries(value).reduce((priceAcc, [storeId, price]) => {
                if (visibleFields.includes(`salePrices.${storeId}`)) {
                  return { ...priceAcc, [storeId]: price }
                }
                return priceAcc
              }, {})
              if (Object.keys(visibleSalePrices).length > 0) {
                acc[key] = visibleSalePrices
              }
            } else if (visibleFields.includes(key)) {
              acc[key] = value
            }
            return acc
          }, {})

          const { errors, formattedProduct } = validateProduct(filteredUpdates, t, {
            currentProductId: product.id,
            lastChangeSource: 'batchEdit',
            organizationId: context.user.organizationId,
            storeId: context.user.store.id,
            userId: context.user.uid
          })
          
          if (errors.length > 0) {
            throw new Error(`${product.name}: ${errors.join(', ')}`)
          }

          await updateProduct(db, product, formattedProduct, productsDict)
        }
      }
      setEditedProducts({})
      
      toast.show(t('success'), {
        type: "custom",
        placement: "bottom",
        duration: 3000,
        offset: 30,
        animationType: "slide-in",
      })
    } catch (error) {
      console.error(error)
      toast.show(error.message || t('error'), {
        type: "custom",
        placement: "bottom",
        duration: 10000,
        offset: 30,
        animationType: "slide-in",
      })
    }
    setIsSaving(false)
  }

  const getValue = (item, field) => {
    if (editedProducts[item.id]?.[field] !== undefined) {
      return editedProducts[item.id][field]
    }
    return item[field]
  }

  const fields = [
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      title: t('productsAndIngredients.name'),
      textCategory: 's1',
      getValue: (item) => getValue(item, 'name')
    },
    {
      key: 'barcode',
      type: 'input',
      title: t('productsAndIngredients.barcode'),
      getValue: (item) => {
        const value = getValue(item, 'barcode')
        return value?.toString() || ''
      },
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'barcode', value)
    },
    {
      key: 'categoryID',
      type: 'select',
      title: t('productsAndIngredients.category'),
      options: Object.values(categoriesDict),
      selectedOption: (item) => categoriesDict[getValue(item, 'categoryID')],
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'categoryID', value.id)
    },
    {
      key: 'supplierID',
      type: 'select',
      title: t('productsAndIngredients.supplier'),
      options: Object.values(suppliersDict),
      selectedOption: (item) => suppliersDict[getValue(item, 'supplierID')],
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'supplierID', value.id)
    },
    {
      key: 'purchasePrice',
      type: 'input',
      keyboardType: 'decimal-pad',
      title: t('productsAndIngredients.purchasePrice'),
      getValue: (item) => {
        const value = getValue(item, 'purchasePrice')
        return isNaN(value) ? '' : value
      },
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'purchasePrice', parseFloat(value) || 0),
      isDisabled: (item) => item.isComposite
    },
    {
      key: 'packagePrice',
      type: 'input',
      keyboardType: 'decimal-pad',
      title: t('productsAndIngredients.additionalSpendingsToPurchasePriceShort'),
      getValue: (item) => {
        const value = getValue(item, 'packagePrice')
        return isNaN(value) ? '' : value
      },
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'packagePrice', parseFloat(value) || 0)
    },
    {
      key: 'productUnit',
      type: 'select',
      title: t('productsAndIngredients.productUnit'),
      options: Object.values(productUnitsDict),
      selectedOption: (item) => productUnitsDict[getValue(item, 'productUnit')],
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'productUnit', value.id)
    },
    {
      key: 'isHiddenInNewSale',
      type: 'toggle',
      title: t('productsAndIngredients.hideInNewSaleScreenShort'),
      getValue: (item) => getValue(item, 'isHiddenInNewSale') || false,
      onChange: (index, value) => handleCellEdit(filteredProducts[index].id, 'isHiddenInNewSale', value)
    },
    ...Object.entries(storesDict).map(([storeId, store]) => ({
      key: `activeInStore.${storeId}`,
      type: 'toggle',
      title: t('productsAndIngredients.enabled') + ' ' + store.name,
      getValue: (item) => {
        const activeInStore = getValue(item, 'activeInStore')
        return activeInStore?.[storeId] ?? true
      },
      onChange: (index, value) => {
        const product = filteredProducts[index]
        handleCellEdit(product.id, 'activeInStore', {
          ...product.activeInStore,
          [storeId]: value
        })
      }
    })),
    ...Object.entries(storesDict).map(([storeId, store]) => ({
      key: `salePrices.${storeId}`,
      type: 'input',
      keyboardType: 'decimal-pad',
      title: t('productsAndIngredients.salePrice') + ' ' + store.name,
      getValue: (item) => {
        const editedSalePrices = editedProducts[item.id]?.salePrices
        if (editedSalePrices) {
          return editedSalePrices[storeId]
        }
        return item.salePrices?.[storeId]
      },
      onChange: (index, value) => {
        const product = filteredProducts[index]
        handleCellEdit(product.id, 'salePrices', {
          ...product.salePrices,
          [storeId]: parseFloat(value) || 0
        })
      }
    }))
  ]

  const activeFields = useMemo(() => 
    fields.filter(field => visibleFields.includes(field.key))
  , [fields, visibleFields])

  const renderHeader = () => {
    // Filter editedProducts to only show products with changes in visible fields
    const visibleEditedProducts = Object.entries(editedProducts).reduce((acc, [productId, updates]) => {
      const hasVisibleChanges = Object.keys(updates).some(key => {
        if (key === 'salePrices') {
          // Check if any visible sale price was changed
          return Object.keys(updates.salePrices).some(storeId => 
            visibleFields.includes(`salePrices.${storeId}`)
          )
        }
        return visibleFields.includes(key)
      })

      if (hasVisibleChanges) {
        acc[productId] = updates
      }
      return acc
    }, {})

    const editedCount = Object.keys(visibleEditedProducts).length;
    const editedNames = Object.keys(visibleEditedProducts)
      .map(id => productsDict[id]?.name)
      .filter(Boolean)
      .join(', ');

    return (
      <View style={styles.filters}>
        <View style={styles.searchRow}>
          <StyledTextInput
            placeholder={t('search')}
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={styles.searchInput}
          />
        </View>

        <Text category="c1">
          {t('productsAndIngredients.filters')}:
        </Text>
        <View style={styles.filtersRow}>
          <PlatformDependantSelect
            data={[
              { id: null, text: t('all') },
              ...Object.values(categoriesDict)
            ]}
            selectedOption={selectedCategory || { id: null, name: t('productsAndIngredients.category') }}
            onSelect={(option) => setSelectedCategory(option.id === null ? null : option)}
            style={styles.filterInput}
            withSearch={false}
            placeholder={t('productsAndIngredients.category')}
          />
          <PlatformDependantSelect
            data={[
              { id: null, text: t('all') },
              ...Object.values(suppliersDict)
            ]}
            selectedOption={selectedSupplier || { id: null, name: t('productsAndIngredients.supplier') }}
            onSelect={(option) => setSelectedSupplier(option.id === null ? null : option)}
            style={styles.filterInput}
            withSearch={false}
            placeholder={t('productsAndIngredients.supplier')}
          />
        </View>

        <View style={styles.fieldsRow}>
          <Text category="c1" style={styles.fieldsLabel}>
            {t('productsAndIngredients.visibleFields')}:
          </Text>
          <View style={styles.checkboxesContainer}>
            {fields.map(field => (
              <CheckBox
                key={field.key}
                checked={visibleFields.includes(field.key)}
                onChange={checked => {
                  if (checked) {
                    setVisibleFields(prev => [...prev, field.key])
                  } else {
                    if (field.key === 'name') return
                    setVisibleFields(prev => prev.filter(key => key !== field.key))
                  }
                }}
                disabled={field.key === 'name'}
                style={styles.checkbox}
              >
                {field.title}
              </CheckBox>
            ))}
          </View>
        </View>

        {editedCount > 0 && (
          <View style={styles.editedSummary}>
            <Text category="c1">
              {t('productsAndIngredients.editedItems', { count: editedCount })}: {editedNames}
            </Text>
          </View>
        )}
      </View>
    );
  };

  return (
    <AdaptiveTableCard
      data={filteredProducts}
      fields={activeFields}
      breakpoint={800}
      renderHeader={renderHeader}
      scrollEnabled={true}
      styles={{
        list: {
          padding: 20
        }
      }}
    />
  )
}

const styles = StyleSheet.create({
  filters: {
    flexDirection: 'column',
    gap: 10,
  },
  searchRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  filtersRow: {
    flexDirection: 'row',
    gap: 10,
  },
  searchInput: {
    flex: 1,
  },
  filterInput: {
    flex: 1,
  },
  editedSummary: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  fieldsRow: {
    flexDirection: 'column',
    gap: 5,
    marginBottom: 5,
  },
  fieldsLabel: {
    marginBottom: 5,
  },
  checkboxesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  checkbox: {
    marginRight: 10,
  },
  headerButton: {
    marginRight: 10,
  },
}) 